import { useState, useEffect, useContext } from 'react'
import ProductTitle from '../product-title'
import Image from '@components/ui/image'
import usePrice from '@framework/product/use-price'
import Counter from '@components/ui/counter'
import { useCart } from '@contexts/cart/cart.context'
import { useTranslation } from 'next-i18next'
import ProductBuyNow from '../product-buynow'
import ProductPrice from '../product-price'
import { generateCartItem } from '@utils/generate-cart-item'
import { toast } from 'react-toastify'
import AddToCartButton from '../add-to-cart-button'
import ScrollToTopButton from '@components/home-one/ScrollToTopButton'
import { IUser } from '@contexts/user/user.utils'
import { UserContext } from '@contexts/user/user.context'

const ProductStickyBar = ({
  product,
  data,
  item,
  isSelected,
  onQuantityChange,
  selectedQuantity,
  selectedVariation,
  selectedOptions,
}: any) => {
  const { t: translate } = useTranslation('common')
  const [isVisible, setIsVisible] = useState(false)
  const [quantity, setQuantity] = useState(selectedQuantity)
  const { addItemToCart, isInCart, getItemFromCart, isInStock, items } =
    useCart()
  const { addToCartLoader } = useContext(UserContext) as IUser
  const { price, basePrice, discount } = usePrice(
    data && {
      amount: data.sale_price ? data.sale_price : data.price,
      baseAmount: data.price,
      currencyCode: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD',
    },
  )
  const handleScroll = () => {
    const scrollTop = window.scrollY
    if (scrollTop > 100) {
      // Adjust the scroll value to show the bar as needed
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }
  const updateQuantityInParent = (newQuantity: number) => {
    setQuantity(newQuantity)
    onQuantityChange(newQuantity)
  }
  function addToCart() {
    if (!isSelected) return
    if (data?.min_sale_qty && quantity < data?.min_sale_qty) {
      if (!items.find((item) => item.id === data.sku)) {
        toast.error(
          `${translate('text-fewest-you-may-purchase')} ${data?.min_sale_qty}`,
          {
            className: 'toast-error',
          },
        )
        return
      }
    }

    const item = generateCartItem(data!, selectedVariation)
    const updatedItem = {
      ...item,
    }

    addItemToCart(updatedItem, quantity, selectedOptions)
  }

  const inputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    let numberFlag = isNaN(parseInt(event.target.value))
    let newValue = numberFlag ? 1 : parseInt(event.target.value)
    if (newValue < 1) return false
    updateQuantityInParent(newValue)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const showBuyNow = process.env.NEXT_PUBLIC_SHOW_BUY_NOW === 'true'
  const handleQuanityIncreament = () => {
    updateQuantityInParent(quantity + 1)
  }
  const handleQuanityDecreament = () => {
    updateQuantityInParent(quantity > 1 ? quantity - 1 : 1)
  }

  useEffect(() => {
    updateQuantityInParent(quantity) // Sync quantity with the parent's selectedQuantity
  }, [quantity])
  useEffect(() => {
    updateQuantityInParent(selectedQuantity) // Sync quantity with the parent's selectedQuantity
  }, [selectedQuantity])

  return (
    <div
      className={`shadow-[0_1px_7px_0px_rgba(0,0,0,0.3)] p-4 lg:p-0 lg:shadow-[0_35px_60px_15px_rgba(0,0,0,0.3) fixed top-auto lg:top-auto bottom-0 left-0 z-10 right-0 bg-brand-light text-white text-center px:1 md:px-4 transition-transform transform 
  ${isVisible ? 'translate-y-0' : 'translate-y-full'}
  `}
    >
      <div>
        <ScrollToTopButton hasStickyBar={true} />
      </div>
      <div className="grid-cols-12 grid items-center gap-2 lg:gap-8 relative">
        <div className="col-span-12  py-2 lg:p-0 lg:col-span-8 flex flex-col items-start [@media_(min-width:576px)]:flex-row  [@media_(min-width:576px)]:items-center justify-between ">
          <div className="flex items-center pt-3">
            <Image
              src={item?.image}
              alt={data?.name!}
              width={60}
              height={60}
              style={{ width: 'auto' }}
              className="mr-5 hidden lg:block max-h-24"
              priority
            />
            <ProductTitle
              product={product}
              className="text-sm md:text-2xl leading-4 pr-1 lg:pr-8"
              headingLevel="h2"
            />
          </div>
          <div className="flex justify-between items-center [@media_(max-width:576px)]:w-full">
            <ProductPrice data={data} />
            <div className="[@media_(min-width:576px)]:hidden mt-2 [@media_(max-width:576px)]:w-[100px] ml-3  counter_container">
              <Counter
                variant="single"
                value={quantity}
                onIncrement={handleQuanityIncreament}
                onDecrement={handleQuanityDecreament}
                onInputChange={inputChangeHandler}
                disabled={
                  isInCart(item.id)
                    ? getItemFromCart(item.id).quantity + quantity >=
                      Number(item.stock)
                    : quantity >= Number(item.stock)
                }
              />
            </div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 flex items-center h-10 sm:h-12 gap-1 pr-0 md:pr-5">
          <div className="[@media_(max-width:576px)]:hidden [@media_(max-width:576px)]:top-[40%] [@media_(max-width:576px)]:right-0">
            <Counter
              variant="single"
              value={quantity}
              onIncrement={() => setQuantity((prev) => prev + 1)}
              onDecrement={() =>
                setQuantity((prev) => (prev !== 1 ? prev - 1 : 1))
              }
              onInputChange={inputChangeHandler}
              disabled={
                isInCart(item.id)
                  ? getItemFromCart(item.id).quantity + quantity >=
                    Number(item.stock)
                  : quantity >= Number(item.stock)
              }
            />
          </div>
          <AddToCartButton
            addToCart={addToCart}
            addToCartLoader={addToCartLoader}
            isSelected={isSelected}
            data={data}
            className="!px-1 mx-[2px] [@media_(max-width:576px)]:flex-1 [@media_(max-width:576px)]:text-xs"
          />

          {showBuyNow && (
            <ProductBuyNow
              data={data}
              quantity={quantity}
              className={
                ' [@media_(max-width:576px)]:flex-1 [@media_(max-width:576px)]:text-xs'
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductStickyBar
